/**
 * Global filter bar for applying filters across all data views.
 *
 * Uses the hierarchical filter system where global filters are configured
 * per study and applied via subject master join.
 */

import { useState, useMemo, useCallback } from 'react'
import { Filter, X, RotateCcw, ChevronDown, Search, Calendar, Bookmark } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import {
  useFilterStore,
  useGlobalFilters,
  useFilterConfig,
  useCachedGlobalValues,
  FilterValue,
  GlobalFilterConfig,
} from '@/stores/filterStore'
import { useStudyStore } from '@/stores/studyStore'
import { SaveFilterDialog } from '@/components/filters/SaveFilterDialog'
import { LoadFilterDropdown } from '@/components/filters/LoadFilterDropdown'

// =============================================================================
// Filter Chip Component
// =============================================================================

interface FilterChipProps {
  label: string
  value: FilterValue
  onRemove: () => void
  onClick: () => void
}

function FilterChip({ label, value, onRemove, onClick }: FilterChipProps) {
  const formatValue = (val: FilterValue): string => {
    if (val === null || val === undefined) return ''
    if (Array.isArray(val)) {
      if (val.length === 0) return ''
      if (val.length === 1) return String(val[0])
      return `${val.length} selected`
    }
    if (typeof val === 'object' && val !== null && ('start' in val || 'end' in val)) {
      const dateRange = val as { start?: string; end?: string }
      if (dateRange.start && dateRange.end) {
        return `${dateRange.start} - ${dateRange.end}`
      }
      if (dateRange.start) return `From ${dateRange.start}`
      if (dateRange.end) return `Until ${dateRange.end}`
      return ''
    }
    return String(val)
  }

  const displayValue = formatValue(value)
  if (!displayValue) return null

  return (
    <Badge
      variant="secondary"
      className="gap-1 pr-1 cursor-pointer hover:bg-secondary/80"
      onClick={onClick}
    >
      <span className="font-medium">{label}:</span>
      <span className="max-w-32 truncate">{displayValue}</span>
      <Button
        variant="ghost"
        size="icon"
        className="h-4 w-4 ml-1 hover:bg-transparent"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
      >
        <X className="h-3 w-3" />
        <span className="sr-only">Remove filter</span>
      </Button>
    </Badge>
  )
}

// =============================================================================
// Multi-Select Filter Dialog
// =============================================================================

interface MultiSelectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  config: GlobalFilterConfig
  value: string[] | null
  options: string[]
  onChange: (value: string[] | null) => void
}

function MultiSelectDialog({
  open,
  onOpenChange,
  config,
  value,
  options,
  onChange,
}: MultiSelectDialogProps) {
  const [search, setSearch] = useState('')
  const selectedValues = value || []

  const filteredOptions = useMemo(() => {
    if (!search) return options
    const lower = search.toLowerCase()
    return options.filter((opt) => opt.toLowerCase().includes(lower))
  }, [options, search])

  const handleToggle = (option: string) => {
    const isSelected = selectedValues.includes(option)
    if (isSelected) {
      const newValues = selectedValues.filter((v) => v !== option)
      onChange(newValues.length > 0 ? newValues : null)
    } else {
      onChange([...selectedValues, option])
    }
  }

  const handleSelectAll = () => {
    onChange(filteredOptions)
  }

  const handleClear = () => {
    onChange(null)
  }

  const handleDone = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md overflow-hidden">
        <DialogHeader>
          <DialogTitle>Filter by {config.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 overflow-hidden">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${config.label.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="h-60 rounded-md border overflow-y-auto overflow-x-hidden">
            <div className="p-2 space-y-1">
              {filteredOptions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No options found
                </p>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option)
                  return (
                    <label
                      key={option}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer hover:bg-accent',
                        isSelected && 'bg-accent'
                      )}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggle(option)}
                        className="shrink-0"
                      />
                      <span className="text-sm break-all" title={option}>{option}</span>
                    </label>
                  )
                })
              )}
            </div>
          </div>
          <div className="flex justify-between">
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={handleClear}>
                Clear
              </Button>
            </div>
            <Button size="sm" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Search Filter Dialog
// =============================================================================

interface SearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  config: GlobalFilterConfig
  value: string | null
  onChange: (value: string | null) => void
}

function SearchDialog({
  open,
  onOpenChange,
  config,
  value,
  onChange,
}: SearchDialogProps) {
  const [inputValue, setInputValue] = useState(value || '')

  const handleApply = () => {
    onChange(inputValue.trim() || null)
    onOpenChange(false)
  }

  const handleClear = () => {
    setInputValue('')
    onChange(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Search {config.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Enter ${config.label.toLowerCase()}...`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="pl-8"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleApply()
              }}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleClear}>
              Clear
            </Button>
            <Button size="sm" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Date Range Filter Dialog
// =============================================================================

interface DateRangeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  config: GlobalFilterConfig
  value: { start?: string; end?: string } | null
  onChange: (value: { start?: string; end?: string } | null) => void
}

function DateRangeDialog({
  open,
  onOpenChange,
  config,
  value,
  onChange,
}: DateRangeDialogProps) {
  const [startDate, setStartDate] = useState(value?.start || '')
  const [endDate, setEndDate] = useState(value?.end || '')

  const handleApply = () => {
    if (!startDate && !endDate) {
      onChange(null)
    } else {
      onChange({ start: startDate || undefined, end: endDate || undefined })
    }
    onOpenChange(false)
  }

  const handleClear = () => {
    setStartDate('')
    setEndDate('')
    onChange(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Filter by {config.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">From</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">To</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleClear}>
              Clear
            </Button>
            <Button size="sm" onClick={handleApply}>
              Apply
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Filter Button Component
// =============================================================================

interface FilterButtonProps {
  config: GlobalFilterConfig
  value: FilterValue
  options: string[]
  onChange: (value: FilterValue) => void
  onRemove: () => void
}

function FilterButton({ config, value, options, onChange, onRemove }: FilterButtonProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [draftValue, setDraftValue] = useState<FilterValue>(value)

  // Sync draft when the dialog opens
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDraftValue(value)
    } else {
      // Commit draft to store when dialog closes (Done / click-away)
      onChange(draftValue)
    }
    setDialogOpen(open)
  }

  const renderDialog = () => {
    switch (config.filterType) {
      case 'multi_select':
        return (
          <MultiSelectDialog
            open={dialogOpen}
            onOpenChange={handleOpenChange}
            config={config}
            value={draftValue as string[] | null}
            options={options}
            onChange={setDraftValue}
          />
        )
      case 'search':
        return (
          <SearchDialog
            open={dialogOpen}
            onOpenChange={handleOpenChange}
            config={config}
            value={draftValue as string | null}
            onChange={setDraftValue}
          />
        )
      case 'date_range':
        return (
          <DateRangeDialog
            open={dialogOpen}
            onOpenChange={handleOpenChange}
            config={config}
            value={draftValue as { start?: string; end?: string } | null}
            onChange={setDraftValue}
          />
        )
      default:
        return null
    }
  }

  // Check if filter has a value
  const hasValue = value !== null && value !== undefined &&
    !(Array.isArray(value) && value.length === 0) &&
    value !== ''

  if (hasValue) {
    return (
      <>
        <FilterChip
          label={config.label}
          value={value}
          onRemove={onRemove}
          onClick={() => setDialogOpen(true)}
        />
        {renderDialog()}
      </>
    )
  }

  return null
}

// =============================================================================
// Add Filter Dropdown
// =============================================================================

interface AddFilterDropdownProps {
  configs: GlobalFilterConfig[]
  activeKeys: string[]
  cachedValues: Record<string, string[]>
  onAdd: (key: string, value: FilterValue) => void
}

function AddFilterDropdown({ configs, activeKeys, cachedValues, onAdd }: AddFilterDropdownProps) {
  const [selectedConfig, setSelectedConfig] = useState<GlobalFilterConfig | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingValue, setPendingValue] = useState<FilterValue>(null)

  const availableConfigs = configs.filter(
    (c) => c.isEnabled && !activeKeys.includes(c.filterKey)
  )

  if (availableConfigs.length === 0) return null

  const handleSelect = (config: GlobalFilterConfig) => {
    setSelectedConfig(config)
    setPendingValue(null)
    setDialogOpen(true)
  }

  const handleDialogClose = (open: boolean) => {
    if (!open && selectedConfig && pendingValue !== null) {
      // Apply the filter when dialog closes with a value selected
      onAdd(selectedConfig.filterKey, pendingValue)
    }
    setDialogOpen(open)
    if (!open) {
      setSelectedConfig(null)
      setPendingValue(null)
    }
  }

  const renderDialog = () => {
    if (!selectedConfig) return null

    const options = cachedValues[selectedConfig.filterKey] || []

    switch (selectedConfig.filterType) {
      case 'multi_select':
        return (
          <MultiSelectDialog
            open={dialogOpen}
            onOpenChange={handleDialogClose}
            config={selectedConfig}
            value={pendingValue as string[] | null}
            options={options}
            onChange={setPendingValue}
          />
        )
      case 'search':
        return (
          <SearchDialog
            open={dialogOpen}
            onOpenChange={handleDialogClose}
            config={selectedConfig}
            value={pendingValue as string | null}
            onChange={(val) => {
              setPendingValue(val)
              // For search, apply immediately when user clicks Apply
              if (val !== null && selectedConfig) {
                onAdd(selectedConfig.filterKey, val)
                setDialogOpen(false)
                setSelectedConfig(null)
                setPendingValue(null)
              }
            }}
          />
        )
      case 'date_range':
        return (
          <DateRangeDialog
            open={dialogOpen}
            onOpenChange={handleDialogClose}
            config={selectedConfig}
            value={pendingValue as { start?: string; end?: string } | null}
            onChange={(val) => {
              setPendingValue(val)
              // For date range, apply immediately when user clicks Apply
              if (val !== null && selectedConfig) {
                onAdd(selectedConfig.filterKey, val)
                setDialogOpen(false)
                setSelectedConfig(null)
                setPendingValue(null)
              }
            }}
          />
        )
      default:
        return null
    }
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'date_range':
        return <Calendar className="h-4 w-4 mr-2" />
      case 'search':
        return <Search className="h-4 w-4 mr-2" />
      default:
        return <Filter className="h-4 w-4 mr-2" />
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 gap-1">
            <Filter className="h-3 w-3" />
            <span className="hidden sm:inline">Add Filter</span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {availableConfigs.map((config) => (
            <DropdownMenuItem
              key={config.filterKey}
              onClick={() => handleSelect(config)}
            >
              {getIcon(config.filterType)}
              {config.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {renderDialog()}
    </>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function GlobalFilterBar() {
  const currentStudy = useStudyStore((state) => state.currentStudy)
  const filterConfig = useFilterConfig()
  const globalFilters = useGlobalFilters()
  const cachedValues = useCachedGlobalValues()
  const setGlobalFilter = useFilterStore((state) => state.setGlobalFilter)
  const resetGlobalFilters = useFilterStore((state) => state.resetGlobalFilters)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)

  // Get enabled global filter configs
  const globalFilterConfigs = useMemo(() => {
    return filterConfig?.globalFilters.filter((gf) => gf.isEnabled) || []
  }, [filterConfig])

  // Get active filter keys
  const activeFilterKeys = useMemo(() => {
    return Object.keys(globalFilters).filter((key) => {
      const value = globalFilters[key]
      if (value === null || value === undefined) return false
      if (Array.isArray(value) && value.length === 0) return false
      if (value === '') return false
      return true
    })
  }, [globalFilters])

  // Get count of active filters
  const activeFilterCount = activeFilterKeys.length

  const handleRemoveFilter = useCallback(
    (filterKey: string) => {
      setGlobalFilter(filterKey, null)
    },
    [setGlobalFilter]
  )

  const handleAddFilter = useCallback(
    (filterKey: string, value: FilterValue) => {
      setGlobalFilter(filterKey, value)
    },
    [setGlobalFilter]
  )

  // Show simple placeholder if no study or no filter config
  if (!currentStudy) {
    return null
  }

  // If no filter config, show a simple message or nothing
  if (!filterConfig || globalFilterConfigs.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">No filters configured</span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Filter icon and label */}
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Filter className="h-4 w-4" />
        <span className="text-sm hidden sm:inline">Filters:</span>
      </div>

      {/* Active filter chips */}
      {activeFilterKeys.map((filterKey) => {
        const config = globalFilterConfigs.find((c) => c.filterKey === filterKey)
        if (!config) return null

        const value = globalFilters[filterKey]
        const options = cachedValues[filterKey] || []

        return (
          <FilterButton
            key={filterKey}
            config={config}
            value={value}
            options={options}
            onChange={(newValue) => setGlobalFilter(filterKey, newValue)}
            onRemove={() => handleRemoveFilter(filterKey)}
          />
        )
      })}

      {/* No filters message */}
      {activeFilterCount === 0 && (
        <span className="text-sm text-muted-foreground">None applied</span>
      )}

      {/* Add filter button */}
      <AddFilterDropdown
        configs={globalFilterConfigs}
        activeKeys={activeFilterKeys}
        cachedValues={cachedValues}
        onAdd={handleAddFilter}
      />

      {/* Reset all button */}
      {activeFilterCount > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 text-muted-foreground"
          onClick={resetGlobalFilters}
        >
          <RotateCcw className="h-3 w-3" />
          <span className="hidden sm:inline">Reset</span>
        </Button>
      )}

      {/* Separator */}
      <div className="h-4 w-px bg-border mx-1" />

      {/* Load saved filter dropdown */}
      <LoadFilterDropdown />

      {/* Save filter button */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1 text-muted-foreground"
        onClick={() => setSaveDialogOpen(true)}
      >
        <Bookmark className="h-3 w-3" />
        <span className="hidden sm:inline">Save</span>
      </Button>

      {/* Save filter dialog */}
      <SaveFilterDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
      />
    </div>
  )
}
